import { getPackagePersistId } from '~/components/Vendors/Profile/SetUpFlashOffer/utils';

import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus } from '../../shared/checklistUtils';
import { getStatusLabel, isFiniteNumber } from '../../shared/checklistUtils';

import type {
  DealOptionApprovalChecklistRow,
  DealOptionsInclusionsChecklistRow,
  DealOptionsInclusionsDealRow,
} from './types';

const FLASH_FLEXI_CAMPAIGN_PATTERN = /flexi/i;

// Mirrors Salesforce's Deal_Offer__c.Extra_Night_Margin_Check__c formula (unsynced — see docs/business-rules.md § Flexi Nights Review Flag).
const EXTRA_NIGHT_MARGIN_GREEN_THRESHOLD = 20;

export function buildDealOptionsInclusionsRows({
  dealDataAvailable,
  relatedDeals,
  relatedInclusions,
  relatedInclusionsAvailable,
}: {
  dealDataAvailable: boolean;
  relatedDeals: Array<OpportunityRelatedDeal>;
  relatedInclusions: Array<OpportunityRelatedInclusion>;
  relatedInclusionsAvailable: boolean;
}): Array<DealOptionsInclusionsChecklistRow> {
  const rows: Array<DealOptionsInclusionsChecklistRow> = [];

  const inclusionsByDealSfid = new Map<string, Array<OpportunityRelatedInclusion>>();
  for (const inc of relatedInclusions) {
    const key = inc.deal_offer__c;
    if (!key) {
      continue;
    }
    const existing = inclusionsByDealSfid.get(key) ?? [];
    existing.push(inc);
    inclusionsByDealSfid.set(key, existing);
  }

  for (const deal of relatedDeals) {
    const dealKey = deal.sfid ?? String(deal.id);
    const roomName = deal.room_asset?.name ?? '—';

    if (!dealDataAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked:deal_offer__c:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        totalValue: null,
        type: '',
      });
      continue;
    }

    if (!relatedInclusionsAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked_inclusion:deal_offer__c:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        totalValue: null,
        type: '',
      });
      continue;
    }

    const nights = isFiniteNumber(deal.number_of_nights__c) ? deal.number_of_nights__c : null;

    for (const inc of inclusionsByDealSfid.get(dealKey) ?? []) {
      const rrp = isFiniteNumber(inc.rrp__c) ? inc.rrp__c : null;
      const assetValue = isFiniteNumber(inc.total_value__c) ? inc.total_value__c : null;
      const isDaily = normalizeInclusionType(inc.type__c) === 'daily';
      const expectedValue = isDaily && rrp !== null && nights !== null ? rrp * nights : rrp;
      const rrpMatch = expectedValue !== null && assetValue !== null && expectedValue === assetValue;

      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: inc.inclusion_description__c ?? inc.description__c ?? '',
        id: `inclusion:deal_offer__c:${dealKey}:inclusion__c:${inc.id}`,
        inclusionAssetValue: assetValue,
        dealOptionInclusionRrp: rrp,
        name: inc.name ?? '',
        rrpMatch,
        roomName,
        status: rrpMatch ? 'approved' : 'requires approval',
        statusLabel: getStatusLabel(rrpMatch ? 'approved' : 'requires approval'),
        totalValue: assetValue,
        type: inc.type__c ?? '',
      });
    }
  }

  return rows;
}

export function buildDealOptionsInclusionsDealRows({
  campaignType,
  flatRows,
  packages,
  relatedDeals,
}: {
  campaignType?: string | null;
  flatRows: Array<DealOptionsInclusionsChecklistRow>;
  packages?: Array<App.Package>;
  relatedDeals: Array<OpportunityRelatedDeal>;
}): Array<DealOptionsInclusionsDealRow> {
  const inclusionsByDealId = new Map<string, Array<DealOptionsInclusionsChecklistRow>>();
  for (const row of flatRows) {
    const existing = inclusionsByDealId.get(row.dealId) ?? [];
    existing.push(row);
    inclusionsByDealId.set(row.dealId, existing);
  }

  const packagesByPersistId = new Map(
    (packages ?? []).flatMap((pkg) => {
      const persistId = getPackagePersistId(pkg);
      return persistId ? [[persistId, pkg] as const] : [];
    }),
  );

  const isFlashFlexiCampaign = !!campaignType && FLASH_FLEXI_CAMPAIGN_PATTERN.test(campaignType);

  return relatedDeals.map((deal) => {
    const dealKey = deal.sfid ?? String(deal.id);
    const matchedPackage = packagesByPersistId.get(dealKey);
    const adminFlexibleNights = !!(
      matchedPackage &&
      'flexible_nights' in matchedPackage &&
      matchedPackage.flexible_nights
    );
    const maxExtraNights = isFiniteNumber(deal.max_extra_nights__c)
      ? deal.max_extra_nights__c
      : Number(deal.max_extra_nights__c ?? 0) || 0;
    const extraNightMarginGreen =
      isFiniteNumber(deal.extra_night_margin__c) && deal.extra_night_margin__c >= EXTRA_NIGHT_MARGIN_GREEN_THRESHOLD;
    const hasSupportingFlexiSignal = maxExtraNights > 0 || isFlashFlexiCampaign || extraNightMarginGreen;
    const needsFlexiNightsReview = !adminFlexibleNights && hasSupportingFlexiSignal;
    const status: ChecklistStatus = needsFlexiNightsReview ? 'requires approval' : 'approved';

    return {
      id: dealKey,
      costPrice: deal.cost_price__c ?? null,
      currency: deal.currencyisocode ?? 'AUD',
      dealOptionName: deal.name ?? '',
      inclusions: inclusionsByDealId.get(dealKey) ?? [],
      marginPercent: deal.margin_percent__c ?? null,
      needsFlexiNightsReview,
      rackRate: deal.room_asset?.rack_rate__c ?? null,
      roomAssetLinked: !!(deal.room_asset?.id ?? deal.room_asset?.sfid),
      roomName: deal.room_asset?.name ?? '—',
      sellPrice: deal.price__c ?? null,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

// Gated by the flexi-nights review flag.
export function buildDealOptionApprovalRows(
  dealRows: Array<DealOptionsInclusionsDealRow>,
): Array<DealOptionApprovalChecklistRow> {
  return dealRows.map((deal) => ({ id: deal.id, status: deal.status, statusLabel: deal.statusLabel }));
}

function normalizeInclusionType(type?: string | null) {
  return type?.trim().toLowerCase() ?? '';
}

export function getDealOptionsInclusionsSearchText(row: DealOptionsInclusionsChecklistRow) {
  return [row.dealOptionName, row.name, row.description, row.type, row.roomName, row.statusLabel]
    .filter(Boolean)
    .join(' ');
}
